/*
 * @Author: Ryan
 * @Date: 2026-02-02
 * @Description: Language translation section for AI Image Translator
 */
import { Button } from 'antd';
import Image from 'next/image';
import React, { useMemo, useState } from 'react';

import { useTranslationConfig } from '@/hooks/useTranslationConfig';
import { scrollToTop } from '@/utils/landing/scrollToTop';

interface LanguageCardProps {
  icon?: string;
  name: string;
}

// 箭头图标
const ArrowRightIcon: React.FC = () => (
  <svg viewBox="64 64 896 896" fill="currentColor" width="1em" height="1em">
    <path d="M869 487.8L491.2 159.9c-2.9-2.5-6.6-3.9-10.5-3.9h-88.5c-7.4 0-10.8 9.2-5.2 14l350.2 304H152c-4.4 0-8 3.6-8 8v60c0 4.4 3.6 8 8 8h585.1L386.9 854c-5.6 4.9-2.2 14 5.2 14h91.5c1.9 0 3.8-.7 5.2-2L869 536.2a32.07 32.07 0 000-48.4z" />
  </svg>
);

const LanguageCard: React.FC<LanguageCardProps> = ({ icon, name }) => {
  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-white rounded-lg border border-gray-200">
      <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden shadow-sm">
        {icon ? (
          <Image
            src={icon}
            alt={name}
            width={40}
            height={40}
            className="w-full h-full object-cover"
            loading="lazy"
            unoptimized
          />
        ) : (
          <div className="w-full h-full bg-gray-300 rounded-full" />
        )}
      </div>
      <span className="text-sm text-gray-800">{name}</span>
    </div>
  );
};

const LanguageSection: React.FC = () => {
  // 使用简单的 mock t 函数获取英文版本
  const [, targetLang] = useTranslationConfig((key: string) => key);

  // 过滤掉没有 icon 的语言
  const languages = useMemo(() => {
    return targetLang.filter((lang) => lang.icon);
  }, [targetLang]);

  const [showAll, setShowAll] = useState(false);

  const visibleLanguages = showAll ? languages : languages.slice(0, 15);

  const handleStartClick = () => {
    scrollToTop();
  };

  return (
    <section className="w-full bg-white py-[90px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-3 pad:mb-4">
          Translate Images into {languages?.length}+ Languages Instantly
        </h2>

        {/* Description */}
        <p className="text-sm pad:text-base text-center text-gray-600 max-w-4xl mx-auto mb-6 pad:mb-8">
          Automatically translate text in images into 30+ languages while preserving the
          original layout and design. Break language barriers and share your visual
          content with the world in seconds.
        </p>

        {/* CTA Button */}
        <div className="flex justify-center mb-8 pad:mb-12">
          <button
            type="button"
            onClick={handleStartClick}
            className="inline-flex items-center justify-center gap-2 px-8 py-4 text-base font-semibold text-white rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg"
            style={{
              background: 'linear-gradient(90deg, #4D2FFF 0%, #EF5CFF 100%)',
            }}
          >
            <span>Translate Image Now</span>
            <ArrowRightIcon />
          </button>
        </div>

        {/* Languages Grid */}
        <div className="bg-[#FCFCFC] rounded-2xl p-4 pad:p-8 mb-4 pad:mb-6">
          <div className="grid grid-cols-1 pad:grid-cols-2 sm:grid-cols-3 gap-3 pad:gap-4">
            {visibleLanguages.map((lang, index) => (
              <LanguageCard key={index} icon={lang.icon} name={lang.name} />
            ))}
          </div>
        </div>

        {/* See all languages link */}
        {!showAll && (
          <div className="text-center">
            <Button type="link" onClick={() => setShowAll(true)}>
              See All Languages
              <span className="text-xs">&gt;&gt;</span>
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default LanguageSection;
